import {
  Checkbox,
  Divider,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiHexaBase } from '../../api/hexa/ApiHexaBase';
import {
  HexaInterestArea,
  LOCAL_POLITICIANS_COUNTRIES,
} from '../../api/hexa/models/SharedModels';
import Grid2 from '@mui/material/Unstable_Grid2';

interface InterestAreasSelectProps {
  value: HexaInterestArea[];
  onChange: (value: HexaInterestArea[]) => void;
}

const SELECT_ALL_BASE = '__select_all_base__';
const SELECT_ALL_LP = '__select_all_lp__';

const InterestAreasSelect = ({ value, onChange }: InterestAreasSelectProps) => {
  const { t } = useTranslation();

  const allAreas = ApiHexaBase.INTEREST_AREAS;
  const baseAreas = allAreas.filter(
    (ia) => !ia.startsWith('local_politicians_')
  );
  const lpAreas = allAreas.filter((ia) =>
    ia.startsWith('local_politicians_')
  );

  const selectedBase = value.filter((v) => !v.startsWith('local_politicians_'));
  const selectedLp = value.filter((v) => v.startsWith('local_politicians_'));

  const allBaseSelected = baseAreas.every((ia) => value.includes(ia));
  const allLpSelected =
    lpAreas.length > 0 && lpAreas.every((ia) => value.includes(ia));

  const getCountryLabel = (ia: HexaInterestArea): string => {
    const code = ia.replace('local_politicians_', '');
    return LOCAL_POLITICIANS_COUNTRIES[code] ?? code.toUpperCase();
  };

  const handleBaseChange = (event: SelectChangeEvent<HexaInterestArea[]>) => {
    const raw = event.target.value;
    const sel = typeof raw === 'string' ? raw.split(',') : raw;

    if (sel.includes(SELECT_ALL_BASE as any)) {
      const newBase = allBaseSelected ? [] : [...baseAreas];
      onChange([...newBase, ...selectedLp]);
      return;
    }
    onChange([...(sel as HexaInterestArea[]), ...selectedLp]);
  };

  const handleLpChange = (event: SelectChangeEvent<HexaInterestArea[]>) => {
    const raw = event.target.value;
    const sel = typeof raw === 'string' ? raw.split(',') : raw;

    if (sel.includes(SELECT_ALL_LP as any)) {
      const newLp = allLpSelected ? [] : [...lpAreas];
      onChange([...selectedBase, ...newLp]);
      return;
    }
    onChange([...selectedBase, ...(sel as HexaInterestArea[])]);
  };

  return (
    <>
      <Grid2 xs={1} sx={{ display: 'flex' }}>
        <Typography variant="body1" >
          {t('hexa_api.fields.interest_areas')}
        </Typography>
      </Grid2>
      <Grid2 xs={1}>
        <Select
          fullWidth
          displayEmpty
          size="small"
          value={selectedBase}
          onChange={handleBaseChange}
          multiple
          MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
          renderValue={(selected: HexaInterestArea[]) => {
            if (selected.length === 0) {
              return (
                <Typography color="gray">
                  {t('hexa_api.fields.select_placeholder')}
                </Typography>
              );
            }
            return selected.join(', ');
          }}
        >
          <MenuItem value={SELECT_ALL_BASE}>
            <Checkbox
              checked={allBaseSelected}
              indeterminate={selectedBase.length > 0 && !allBaseSelected}
              size="small"
            />
            <ListItemText
              primary={allBaseSelected ? t('deselect_all') : t('select_all')}
            />
          </MenuItem>
          <Divider />
          {baseAreas.map((ia) => (
            <MenuItem key={ia} value={ia}>
              <Checkbox checked={selectedBase.includes(ia)} size="small" />
              <ListItemText primary={ia} />
            </MenuItem>
          ))}
        </Select>
      </Grid2>
      <Grid2 xs={1}>
        <Typography variant="body1">
          {t('hexa_api.fields.local_politicians_by_country')}
        </Typography>
      </Grid2>
      <Grid2 xs={1}>
        <Select
          fullWidth
          displayEmpty
          size="small"
          value={selectedLp}
          onChange={handleLpChange}
          multiple
          MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
          renderValue={(selected: HexaInterestArea[]) => {
            if (selected.length === 0) {
              return (
                <Typography color="gray">
                  {t('hexa_api.fields.select_placeholder')}
                </Typography>
              );
            }
            return selected.map((ia) => getCountryLabel(ia)).join(', ');
          }}
        >
          <MenuItem value={SELECT_ALL_LP}>
            <Checkbox
              checked={allLpSelected}
              indeterminate={selectedLp.length > 0 && !allLpSelected}
              size="small"
            />
            <ListItemText
              primary={allLpSelected ? t('deselect_all') : t('select_all')}
            />
          </MenuItem>
          <Divider />
          {lpAreas.map((ia) => (
            <MenuItem key={ia} value={ia}>
              <Checkbox checked={selectedLp.includes(ia)} size="small" />
              <ListItemText primary={getCountryLabel(ia)} secondary={ia} />
            </MenuItem>
          ))}
        </Select>
      </Grid2>
    </>
  );
};

export default InterestAreasSelect;
